import React, { useState, useEffect, useRef } from 'react';
import { ArrowUpRight, Users, Award, TrendingUp, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { aboutContent, stats } from '../../data/mock';

const AnimatedCounter = ({ end, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const duration = 2000;
    const steps = 60;
    const stepValue = end / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += stepValue;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isVisible, end]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const AboutSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const features = [
    'SAP Certified Consultants',
    '24/7 Global Support',
    'Agile Development Process',
    'ISO 27001 Compliant'
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="reveal-left">
            <span className="inline-block px-4 py-1.5 bg-[#ff6b35]/10 text-[#ff6b35] text-sm font-semibold rounded-full mb-4">
              About Us
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Your Trusted Technology Partner
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              {aboutContent.description} {aboutContent.details}
            </p>

            {/* Features List */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-gray-700 font-medium">{feature}</span>
                </div>
              ))}
            </div>

            <Link
              to="/about"
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-full font-semibold hover:bg-gray-800 transition-colors"
            >
              Learn More About Us
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Right Content - Stats Cards */}
          <div className="reveal-right delay-200">
            <div className="grid grid-cols-2 gap-5">
              {/* Stats Card 1 */}
              <div className="bg-gradient-to-br from-[#ff6b35] to-[#ff8f5c] rounded-3xl p-8 text-white">
                <TrendingUp className="w-10 h-10 mb-4 opacity-80" />
                <div className="text-5xl font-bold mb-2">
                  <AnimatedCounter end={parseInt(stats.projectsDone)} suffix="+" />
                </div>
                <div className="text-white/80">Projects Completed</div>
              </div>

              {/* Stats Card 2 */}
              <div className="bg-gray-900 rounded-3xl p-8 text-white">
                <Users className="w-10 h-10 mb-4 text-[#ff6b35]" />
                <div className="text-5xl font-bold mb-2">
                  <AnimatedCounter end={parseInt(stats.customers.replace('+', ''))} suffix="+" />
                </div>
                <div className="text-gray-400">Happy Clients</div>
              </div>

              {/* Stats Card 3 - Full Width */}
              <div className="col-span-2 bg-gray-50 rounded-3xl p-8 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <Award className="w-10 h-10 mb-4 text-[#ff6b35]" />
                    <div className="text-5xl font-bold text-gray-900 mb-2">
                      <AnimatedCounter end={parseInt(stats.resultsGuaranteed)} suffix="%" />
                    </div>
                    <div className="text-gray-600">Client Satisfaction Rate</div>
                  </div>
                  <div className="flex -space-x-3">
                    {aboutContent.avatars.map((avatar, i) => (
                      <div key={i} className="w-12 h-12 rounded-full border-3 border-white overflow-hidden shadow-lg">
                        <img src={avatar} alt="Client" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
